// ============================================================================
// Generate Documents — single dropdown button replacing the legacy
// "Print / Save as PDF" link in SoapSummary.
//
// Flow:
//   Click bold button → menu of 7 items
//      • Encounter Summary (opens existing /print page in new tab)
//      • Sick Certificate / Prescription / Lab / Imaging / Referral / Invoice
//   For sick cert + invoice, an input modal opens first.
//   For all 6 generated types, on success the PostGenerationModal pops
//   with three big actions: Open · Print · Email.
//   The bottom of the menu lists previously-generated docs so the
//   doctor can re-open / re-email without regenerating.
// ============================================================================

import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  GENERATED_DOCUMENT_TYPES,
  generateDocument,
  listGeneratedDocuments,
} from '../../../api/documents.js'
import { formatDateTime } from '../../../utils/format.js'

import SickCertModal from './SickCertModal.jsx'
import InvoiceModal from './InvoiceModal.jsx'
import MedicalReportModal from './MedicalReportModal.jsx'
import PostGenerationModal from './PostGenerationModal.jsx'
import EmailDocumentModal from './EmailDocumentModal.jsx'

// Visual icons (text-based for portability — render fine in every
// browser without an icon library dependency).
const ICONS = {
  encounter_summary: '📋',
  medical_report:    '📑',
  sick_certificate:  '🏥',
  prescription:      '💊',
  lab_request:       '🧪',
  imaging_request:   '🩻',
  referral_letter:   '✉️',
  invoice:           '💵',
}

export default function GenerateDocumentsMenu({ encounter }) {
  const qc = useQueryClient()
  const wrapperRef = useRef(null)

  const [open, setOpen] = useState(false)
  const [activeForm, setActiveForm] = useState(null)         // 'sick_certificate' | 'invoice' | null
  const [postGenDoc, setPostGenDoc] = useState(null)         // ClinicalDocument row
  const [postGenLabel, setPostGenLabel] = useState('')
  const [emailDoc, setEmailDoc] = useState(null)
  const [error, setError] = useState(null)

  const { data: docs = [], refetch } = useQuery({
    queryKey: ['generated-documents', encounter?.id],
    queryFn: () => listGeneratedDocuments(encounter.id),
    enabled: Boolean(encounter?.id),
    staleTime: 15_000,
  })

  // Close the dropdown on outside-click. We don't close on inner clicks
  // because the menu items themselves manage state.
  useEffect(() => {
    if (!open) return
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const generate = useMutation({
    mutationFn: (args) => generateDocument(args),
    onSuccess: (doc, args) => {
      setError(null)
      setActiveForm(null)
      refetch()
      qc.invalidateQueries({ queryKey: ['encounter-documents', encounter?.id] })
      // Hand off to the post-generation action modal.
      const t = GENERATED_DOCUMENT_TYPES.find((x) => x.kind === args.document_type)
      setPostGenLabel(t?.label || args.document_type)
      setPostGenDoc(doc)
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.detail
        || err?.response?.data?.document_type
        || err?.response?.data?.encounter_id
        || err?.message
        || 'Failed to generate document.'
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg))
    },
  })

  if (!encounter?.id) return null

  // Pre-flight: refuse to enable the buttons whose required data is missing.
  // The backend re-checks server-side and returns a clean 422 either way.
  const has = {
    rx:       (encounter.prescriptions || []).length > 0,
    lab:      (encounter.lab_orders   || []).length > 0,
    imaging:  (encounter.imaging_orders || []).length > 0,
    referral: (encounter.referrals    || []).length > 0,
  }
  const requirementHint = (kind) => {
    if (kind === 'prescription'    && !has.rx)      return 'Add a prescription first'
    if (kind === 'lab_request'     && !has.lab)     return 'Add a lab order first'
    if (kind === 'imaging_request' && !has.imaging) return 'Add an imaging order first'
    if (kind === 'referral_letter' && !has.referral) return 'Add a referral first'
    return ''
  }
  const isDisabled = (kind) => Boolean(requirementHint(kind))

  const handlePick = (kind) => {
    setError(null)
    if (isDisabled(kind)) return
    if (kind === 'encounter_summary') {
      // Existing /print page — opens in new tab, no API call.
      window.open(`/doctor/encounters/${encounter.id}/print`, '_blank', 'noopener,noreferrer')
      setOpen(false)
      return
    }
    const t = GENERATED_DOCUMENT_TYPES.find((x) => x.kind === kind)
    if (t?.needsForm) {
      setActiveForm(kind)
    } else {
      generate.mutate({ document_type: kind, encounter_id: encounter.id })
    }
    setOpen(false)
  }

  return (
    <div ref={wrapperRef} className="relative inline-block">
      {/* The bold button — replaces "Print / Save as PDF" */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 bg-wantok-700 hover:bg-wantok-800 text-white font-bold text-sm rounded-lg px-4 py-2.5 shadow-md hover:shadow-lg transition"
      >
        <span className="text-base">📄</span>
        <span>Generate Documents</span>
        <span className={'transition ' + (open ? 'rotate-180' : '')}>▾</span>
      </button>

      {/* Inline error toast */}
      {error && (
        <div className="absolute right-0 mt-2 w-80 z-40 bg-rose-50 border-2 border-rose-200 text-rose-800 rounded-lg px-3 py-2 text-xs font-medium shadow">
          {error}
        </div>
      )}

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border-2 border-slate-200 rounded-xl shadow-2xl z-30 overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b-2 border-slate-100">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Generate</div>
          </div>

          {/* Encounter summary — special, links to existing /print */}
          <MenuItem
            icon={ICONS.encounter_summary}
            label="Encounter Summary"
            hint="The full clinical record"
            onClick={() => handlePick('encounter_summary')}
          />

          <div className="border-t border-slate-100" />

          {/* Six generated types */}
          {GENERATED_DOCUMENT_TYPES.map((t) => (
            <MenuItem
              key={t.kind}
              icon={ICONS[t.kind]}
              label={t.label}
              hint={requirementHint(t.kind) || (t.needsForm ? 'A few quick details next' : 'One-click — auto-filled')}
              disabled={isDisabled(t.kind)}
              onClick={() => handlePick(t.kind)}
            />
          ))}

          {/* Recently generated */}
          {docs.length > 0 && (
            <>
              <div className="px-4 py-2 bg-slate-50 border-t-2 border-b border-slate-100">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Recent</div>
              </div>
              <div className="max-h-56 overflow-y-auto">
                {docs.slice(0, 6).map((d) => (
                  <RecentItem
                    key={d.id}
                    doc={d}
                    onOpen={() => {
                      if (d.file_url) window.open(d.file_url, '_blank', 'noopener,noreferrer')
                    }}
                    onEmail={() => { setEmailDoc(d); setOpen(false) }}
                  />
                ))}
              </div>
            </>
          )}

          {generate.isPending && (
            <div className="px-4 py-3 text-xs text-slate-500 border-t-2 border-slate-100 bg-slate-50">
              Rendering PDF…
            </div>
          )}
        </div>
      )}

      {/* Modals layered above the dropdown */}
      {activeForm === 'sick_certificate' && (
        <SickCertModal
          submitting={generate.isPending}
          onClose={() => setActiveForm(null)}
          onSubmit={(payload) =>
            generate.mutate({
              document_type: 'sick_certificate',
              encounter_id: encounter.id,
              payload,
            })
          }
        />
      )}
      {activeForm === 'invoice' && (
        <InvoiceModal
          encounterId={encounter.id}
          submitting={generate.isPending}
          onClose={() => setActiveForm(null)}
          onSubmit={(payload) =>
            generate.mutate({
              document_type: 'invoice',
              encounter_id: encounter.id,
              payload,
            })
          }
        />
      )}
      {activeForm === 'medical_report' && (
        <MedicalReportModal
          submitting={generate.isPending}
          onClose={() => setActiveForm(null)}
          onSubmit={(payload) =>
            generate.mutate({
              document_type: 'medical_report',
              encounter_id: encounter.id,
              payload,
            })
          }
        />
      )}
      {postGenDoc && (
        <PostGenerationModal
          doc={postGenDoc}
          kindLabel={postGenLabel}
          onClose={() => setPostGenDoc(null)}
          onEmail={(d) => { setPostGenDoc(null); setEmailDoc(d) }}
        />
      )}
      {emailDoc && (
        <EmailDocumentModal
          doc={emailDoc}
          encounter={encounter}
          onClose={() => setEmailDoc(null)}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Menu primitives
// ---------------------------------------------------------------------------

function MenuItem({ icon, label, hint, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={
        'w-full text-left px-4 py-3 flex items-start gap-3 transition border-b border-slate-50 last:border-b-0 ' +
        (disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:bg-wantok-50 active:bg-wantok-100')
      }
    >
      <span className="text-2xl shrink-0 leading-none mt-0.5">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block font-bold text-slate-800 text-sm">{label}</span>
        <span className={'block text-xs mt-0.5 ' + (disabled ? 'text-rose-600 font-medium' : 'text-slate-500')}>
          {hint}
        </span>
      </span>
    </button>
  )
}

function RecentItem({ doc, onOpen, onEmail }) {
  const t = GENERATED_DOCUMENT_TYPES.find((x) => x.kind === doc.kind)
  const label = t?.label || doc.kind
  return (
    <div className="px-4 py-2.5 flex items-center justify-between gap-2 border-b border-slate-50 last:border-b-0">
      <div className="flex items-start gap-2 min-w-0">
        <span className="text-xl leading-none mt-0.5 shrink-0">{ICONS[doc.kind] || '📄'}</span>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800 truncate">{label}</div>
          <div className="text-[11px] text-slate-500 truncate">
            {formatDateTime(doc.uploaded_at)}
            {doc.generated_by_username && ` · by ${doc.generated_by_username}`}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={onOpen}
          className="text-[11px] font-semibold text-wantok-700 hover:text-wantok-900 hover:bg-wantok-50 rounded px-2 py-1"
          title="Open in new tab"
        >
          Open
        </button>
        <button
          onClick={onEmail}
          className="text-[11px] font-semibold text-wantok-700 hover:text-wantok-900 hover:bg-wantok-50 rounded px-2 py-1"
          title="Email this document"
        >
          Email
        </button>
      </div>
    </div>
  )
}
